import React, { useState, useEffect } from 'react';
import './DatasetCreation.css';
import { useNavigate } from 'react-router-dom';

interface Dataset {
  id: string;
  name: string;
  description: string;
}

interface SelectedItem {
  id: string | number;
  system_name: string;
}

interface DatasetRedirect {
  id: string;
}

interface DatasetModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCount: number;
  onCreateDataset: (name: string, description: string) => Promise<DatasetRedirect>;
  onAddToDataset: (datasetId: string) => Promise<DatasetRedirect>;
  availableDatasets?: Dataset[];
  isEditMode?: boolean;
  fetchDatasets?: () => Promise<void>;
  selectedImages: SelectedItem[]; 
}

const DatasetModal: React.FC<DatasetModalProps> = ({
  isOpen,
  onClose,
  selectedCount,
  onCreateDataset,
  onAddToDataset,
  availableDatasets = [],
  isEditMode = true,
  fetchDatasets,
  selectedImages
}) => {
  const [datasets, setDatasets] = useState<Dataset[]>(availableDatasets);
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<'create' | 'add'>(isEditMode ? 'add' : 'create');
  const [selectedDatasetId, setSelectedDatasetId] = useState<string>('');
  const navigate = useNavigate();

  // --- NEW: State to store the IDs of images that already exist in the target dataset ---
  const [existingImageKeys, setExistingImageKeys] = useState<Set<string>>(new Set());

  // --- NEW: useEffect to check for existing images when a dataset is selected ---
  useEffect(() => {
    const checkForExistingImages = async () => {
      if (!selectedDatasetId || selectedImages.length === 0) {
        setExistingImageKeys(new Set());
        return;
      }
      console.log('Image data is changing:', selectedImages);
      try {
        // Create the payload with the array of objects
        const payload = {
          dataset_id: parseInt(selectedDatasetId, 10),
          images: selectedImages.map(img => ({
            id: parseInt(img.id.toString(), 10),
            system_name: img.system_name,
          })),
        };

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/datasets/check-images`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Failed to check images');
        
        // The response is now an array of objects
        const existingDuplicates: SelectedItem[] = await response.json();
        
        // Create a Set of composite keys (e.g., "123-systemA") for easy lookup
        const duplicateKeys = new Set(
            existingDuplicates.map(d => `${d.id}-${d.system_name}`)
        );
        setExistingImageKeys(duplicateKeys);

      } catch (err) {
        console.error("Failed to check for existing images:", err);
        setExistingImageKeys(new Set());
      }
    };

    if (isOpen) {
        checkForExistingImages();
    }
  }, [isOpen, mode, selectedDatasetId, selectedImages]); // Rerun whenever these change


  // Fetch datasets from the API
  const loadDatasets = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/dataset_names`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch datasets');
      }
      const data = await response.json();
      setDatasets(data);
    } catch (error) {
      console.error('Error fetching datasets:', error);
      setError('Failed to load available datasets');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Fetch datasets when the modal opens
    if (isOpen) {
      loadDatasets();
    }
  }, [isOpen]);

  // Reset form when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setDescription('');
      setError('');
      setMode(isEditMode ? 'add' : 'create');
      setSelectedDatasetId('');
    }
  }, [isOpen, isEditMode]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      if (mode === 'create') {
        if (!name.trim()) {
          setError('Dataset name is required');
          setIsSubmitting(false);
          return;
        }

        const newDataset = await onCreateDataset(name, description);
        // Reset form
        setName('');
        setDescription('');
        if (newDataset && newDataset.id) {
        navigate(`/dataset-management/${newDataset.id}`);
        }
      } else {
        if (!selectedDatasetId) {
          setError('Please select a dataset');
          setIsSubmitting(false);
          return;
        }

        const newDataset = await onAddToDataset(selectedDatasetId);

        if (newDataset && newDataset.id) {
        navigate(`/dataset-management/${newDataset.id}`);
        }
      }
      
      // Close the modal after successful operation
      onClose();
    } catch (err) {
      setError(`Failed to ${mode === 'create' ? 'create' : 'update'} dataset. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'create' ? 'add' : 'create');
    setError(''); // Clear any previous errors
  };

  // --- NEW: Calculate how many *new* images will be added ---
  const newImagesCount = selectedCount - existingImageKeys.size;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {mode === 'create' ? 'Create New Dataset' : 'Add to Existing Dataset'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={isSubmitting}
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {/* Toggle buttons for create/add modes */}
        <div className="flex mb-4 border rounded-md overflow-hidden">
          <button
            type="button"
            className={`flex-1 py-2 px-4 text-center ${
              mode === 'add'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setMode('add')}
            disabled={isSubmitting}
          >
            Add to Existing
          </button>
          <button
            type="button"
            className={`flex-1 py-2 px-4 text-center ${
              mode === 'create'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setMode('create')}
            disabled={isSubmitting}
          >
            Create New
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {mode === 'create' ? (
            <>
              <div className="mb-4">
                <label htmlFor="dataset-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Dataset Name*
                </label>
                <input
                  id="dataset-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter dataset name"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="dataset-description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  id="dataset-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter dataset description"
                  rows={4}
                  disabled={isSubmitting}
                />
              </div>
            </>
          ) : (
            <div className="mb-4">
              <label htmlFor="dataset-select" className="block text-sm font-medium text-gray-700 mb-1">
                Select Dataset*
              </label>
              {isLoading ? (
                <div className="p-3 bg-gray-50 text-gray-500 rounded-md">
                  Loading datasets...
                </div>
              ) : datasets.length > 0 ? (
                <select
                  id="dataset-select"
                  value={selectedDatasetId}
                  onChange={(e) => setSelectedDatasetId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Select a dataset</option>
                  {datasets.map((dataset) => (
                    <option key={dataset.id} value={dataset.id}>
                      {dataset.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="p-3 bg-yellow-50 text-yellow-700 rounded-md">
                  No existing datasets found. Please create a new dataset.
                </div>
              )}
            </div>
          )}

          <div className="bg-blue-50 p-3 rounded-md mb-4 text-sm space-y-2">
          <p className="text-blue-800">
            <span className="font-semibold">{selectedCount}</span> image(s) selected.
          </p>
          {mode === 'add' && selectedDatasetId && (
            <p className="text-blue-800">
              <span className="font-semibold">{newImagesCount}</span> new image(s) will be added to the selected dataset.
            </p>
          )}
        </div>

          {mode === 'add' && existingImageKeys.size > 0 && (
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-3 rounded-md mb-4 text-sm">
              <p>
                  <span className="font-semibold">{existingImageKeys.size}</span> selected image(s) are already in this dataset and will be skipped.
              </p>
          </div>
        )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 bg-blue-500 text-white rounded-md transition-colors ${
                isSubmitting ? 'opacity-75 cursor-not-allowed' : 'hover:bg-blue-600'
              }`}
              disabled={isSubmitting || (mode === 'add' && !selectedDatasetId)}
            >
              {isSubmitting 
                ? (mode === 'create' ? 'Creating...' : 'Adding...') 
                : (mode === 'create' ? 'Create Dataset' : 'Add to Dataset')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DatasetModal;